import { useEffect, useMemo, useState } from "react";
import AdminCard from "../components/admin/AdminCard";
import AdminTable from "../components/admin/AdminTable";
import Modal from "../components/common/Modal";
import Button from "../components/common/Button";
import Input from "../components/common/Input";
import Loader from "../components/common/Loader";
import { BOOKING_STATUS, SERVICE_MODE } from "../utils/constants";
import { formatDate, formatTime } from "../utils/formatDate";
import formatCurrency from "../utils/formatCurrency";
import * as bookingService from "../services/bookingService";

const safeCallGetAllBookings = async () => {
  if (typeof bookingService.getAllBookings === "function") {
    return bookingService.getAllBookings();
  }

  if (typeof bookingService.getBookings === "function") {
    return bookingService.getBookings();
  }

  if (typeof bookingService.getAllAdminBookings === "function") {
    return bookingService.getAllAdminBookings();
  }

  throw new Error("Booking list method was not found in bookingService.");
};

const safeCallCompleteBooking = async (id) => {
  if (typeof bookingService.completeBooking === "function") {
    return bookingService.completeBooking(id);
  }

  if (typeof bookingService.markBookingCompleted === "function") {
    return bookingService.markBookingCompleted(id);
  }

  throw new Error("Complete booking method was not found in bookingService.");
};

const safeCallCancelBooking = async (id) => {
  if (typeof bookingService.cancelBookingById === "function") {
    return bookingService.cancelBookingById(id);
  }

  if (typeof bookingService.cancelBooking === "function") {
    return bookingService.cancelBooking(id);
  }

  throw new Error("Cancel booking method was not found in bookingService.");
};

const buildOnsiteAddressText = (onsiteAddress) => {
  if (!onsiteAddress) return "—";

  return (
    [
      onsiteAddress.line1,
      onsiteAddress.line2,
      onsiteAddress.city,
      onsiteAddress.state,
      onsiteAddress.zip,
    ]
      .filter(Boolean)
      .join(", ") || "—"
  );
};

const normalizeBookingItems = (booking) => {
  if (Array.isArray(booking?.bookingItems) && booking.bookingItems.length > 0) {
    return booking.bookingItems;
  }

  if (booking?.service) {
    return [
      {
        service: booking.service,
        hennaType: booking.hennaType || booking.service.hennaType || "",
        numOfAdults: booking.numOfAdults ?? 0,
        numOfChildren: booking.numOfChildren ?? 0,
        partySize: booking.partySize ?? 0,
        bookingDurationMinutes: booking.bookingDurationMinutes ?? 0,
        bookingPrice: booking.bookingPrice ?? 0,
        sortOrder: 0,
      },
    ];
  }

  return [];
};

const getBookedServiceNames = (booking) => {
  const items = normalizeBookingItems(booking);

  if (items.length === 0) return "—";

  return items
    .map((item, index) => item?.service?.serviceName || `Service ${index + 1}`)
    .join(", ");
};

const getBookedHennaTypes = (booking) => {
  const items = normalizeBookingItems(booking);

  if (items.length === 0) return "—";

  return items
    .map(
      (item) => item?.hennaType || item?.service?.hennaType || "Henna type not set"
    )
    .join(", ");
};

const ManageBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  const loadBookings = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await safeCallGetAllBookings();
      const data = response?.data || response;

      if (data?.statusCode && data.statusCode !== 200) {
        throw new Error(data.message || "Failed to load bookings.");
      }

      setBookings(Array.isArray(data?.bookingList) ? data.bookingList : []);
    } catch (err) {
      setError(err?.message || "We could not load bookings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
  }, []);

  const sortedBookings = useMemo(() => {
    return [...bookings].sort((a, b) => {
      const aDateTime = new Date(
        `${a?.bookingDate || "1970-01-01"}T${a?.startTime || "00:00"}`
      ).getTime();

      const bDateTime = new Date(
        `${b?.bookingDate || "1970-01-01"}T${b?.startTime || "00:00"}`
      ).getTime();

      return bDateTime - aDateTime;
    });
  }, [bookings]);

  const filteredBookings = useMemo(() => {
    const query = search.trim().toLowerCase();

    return sortedBookings.filter((booking) => {
      const matchesStatus =
        statusFilter === "ALL" ? true : booking?.status === statusFilter;

      const clientOrGuest = booking?.user
        ? `${booking.user.firstname || ""} ${booking.user.lastname || ""} ${booking.user.email || ""}`
        : booking?.guest
        ? `${booking.guest.firstName || ""} ${booking.guest.lastName || ""} ${booking.guest.email || ""}`
        : "";

      const serviceNames = getBookedServiceNames(booking).toLowerCase();
      const hennaTypes = getBookedHennaTypes(booking).toLowerCase();

      const matchesSearch =
        !query ||
        serviceNames.includes(query) ||
        hennaTypes.includes(query) ||
        booking?.bookingConfirmationCode?.toLowerCase().includes(query) ||
        clientOrGuest.toLowerCase().includes(query) ||
        booking?.status?.toLowerCase().includes(query) ||
        booking?.serviceMode?.toLowerCase().includes(query);

      return matchesStatus && matchesSearch;
    });
  }, [sortedBookings, search, statusFilter]);

  const resetMessages = () => {
    setError("");
    setSuccessMessage("");
  };

  const handleStatusAction = async () => {
    if (!confirmAction?.booking?.id || !confirmAction?.type) return;

    try {
      setActionLoading(true);
      resetMessages();

      const response =
        confirmAction.type === "COMPLETE"
          ? await safeCallCompleteBooking(confirmAction.booking.id)
          : await safeCallCancelBooking(confirmAction.booking.id);

      const data = response?.data || response;

      if (data?.statusCode && data.statusCode !== 200) {
        throw new Error(data.message || "Booking action failed.");
      }

      setSuccessMessage(
        data?.message ||
          (confirmAction.type === "COMPLETE"
            ? "The booking has been marked as completed."
            : "The booking has been canceled successfully.")
      );

      setConfirmAction(null);
      await loadBookings();
    } catch (err) {
      setError(err?.message || "We could not update the booking status.");
    } finally {
      setActionLoading(false);
    }
  };

  const getDisplayName = (booking) => {
    if (booking?.user) {
      return (
        `${booking.user.firstname || ""} ${booking.user.lastname || ""}`.trim() ||
        booking.user.email ||
        "User"
      );
    }

    if (booking?.guest) {
      return (
        `${booking.guest.firstName || ""} ${booking.guest.lastName || ""}`.trim() ||
        booking.guest.email ||
        "Guest"
      );
    }

    return "—";
  };

  const getStatusChip = (status) => {
    const className =
      status === BOOKING_STATUS.CONFIRMED
        ? "status-chip status-chip-confirmed"
        : status === BOOKING_STATUS.CANCELED
        ? "status-chip status-chip-canceled"
        : status === BOOKING_STATUS.COMPLETED
        ? "status-chip status-chip-completed"
        : "status-chip";

    return <span className={className}>{status || "—"}</span>;
  };

  return (
    <div className="space-y-6">
      {loading ? <Loader text="Loading booking records..." /> : null}

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-medium text-red-700">{error}</p>
        </div>
      ) : null}

      {successMessage ? (
        <div className="rounded-2xl border border-green-200 bg-green-50 p-4">
          <p className="text-sm font-medium text-green-700">{successMessage}</p>
        </div>
      ) : null}

      <AdminCard
        title="Manage Bookings"
        subtitle="Review appointment records, inspect details, and update booking status when needed."
        actions={
          <div className="flex flex-col gap-3 md:flex-row">
            <Input
              name="bookingSearch"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by service, client, guest, code, or status..."
              className="min-w-[240px]"
            />

            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="min-h-[46px] rounded-xl border border-goldlight bg-white px-4 py-3 text-sm text-[#2f2626]"
            >
              <option value="ALL">All Statuses</option>
              <option value={BOOKING_STATUS.CONFIRMED}>CONFIRMED</option>
              <option value={BOOKING_STATUS.CANCELED}>CANCELED</option>
              <option value={BOOKING_STATUS.COMPLETED}>COMPLETED</option>
            </select>
          </div>
        }
      >
        <AdminTable
          loading={loading}
          data={filteredBookings}
          emptyMessage="No bookings match the current filters."
          className="max-h-[500px] overflow-y-auto"
          columns={[
            {
              key: "service",
              label: "Services",
              render: (row) => getBookedServiceNames(row),
            },
            {
              key: "hennaType",
              label: "Henna Types",
              render: (row) => getBookedHennaTypes(row),
            },
            {
              key: "client",
              label: "Client / Guest",
              render: (row) => getDisplayName(row),
            },
            {
              key: "bookingDate",
              label: "Date",
              render: (row) =>
                row?.bookingDate ? formatDate(row.bookingDate) : "—",
            },
            {
              key: "time",
              label: "Time",
              render: (row) =>
                row?.startTime && row?.endTime
                  ? `${formatTime(row.startTime)} - ${formatTime(row.endTime)}`
                  : "—",
            },
            {
              key: "serviceMode",
              label: "Mode",
              render: (row) => row?.serviceMode || "—",
            },
            {
              key: "price",
              label: "Total",
              render: (row) =>
                row?.bookingPrice !== undefined && row?.bookingPrice !== null
                  ? formatCurrency(row.bookingPrice)
                  : "—",
            },
            {
              key: "status",
              label: "Status",
              render: (row) => getStatusChip(row?.status),
            },
          ]}
          actions={(row) => {
            const actionList = [
              {
                label: "View",
                variant: "secondary",
                onClick: () => setSelectedBooking(row),
              },
            ];

            if (row?.status === BOOKING_STATUS.CONFIRMED) {
              actionList.push(
                {
                  label: "Complete",
                  variant: "primary",
                  onClick: () =>
                    setConfirmAction({
                      type: "COMPLETE",
                      booking: row,
                    }),
                },
                {
                  label: "Cancel",
                  variant: "danger",
                  onClick: () =>
                    setConfirmAction({
                      type: "CANCEL",
                      booking: row,
                    }),
                }
              );
            }

            return actionList;
          }}
        />
      </AdminCard>

      <Modal
        isOpen={Boolean(selectedBooking)}
        onClose={() => setSelectedBooking(null)}
        title="Booking Details"
        size="lg"
        footer={
          <div className="flex justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setSelectedBooking(null)}
            >
              Close
            </Button>
          </div>
        }
      >
        {selectedBooking ? (
          <div className="space-y-5">
            <div className="form-grid two">
              <div className="rounded-2xl bg-soft-cream p-4">
                <p className="text-sm font-semibold text-henna">Booked Services</p>
                <p className="mt-2 text-sm leading-6 text-[#3b2f2f]">
                  {getBookedServiceNames(selectedBooking)}
                </p>
              </div>

              <div className="rounded-2xl bg-soft-cream p-4">
                <p className="text-sm font-semibold text-henna">Status</p>
                <div className="mt-2">{getStatusChip(selectedBooking?.status)}</div>
              </div>
            </div>

            <div className="form-grid two">
              <div>
                <p className="text-sm font-semibold text-[#3b2f2f]">Client / Guest</p>
                <p className="mt-2 text-sm text-muted-warm">
                  {getDisplayName(selectedBooking)}
                </p>
              </div>

              <div>
                <p className="text-sm font-semibold text-[#3b2f2f]">
                  Confirmation Code
                </p>
                <p className="mt-2 text-sm text-muted-warm">
                  {selectedBooking?.bookingConfirmationCode || "—"}
                </p>
              </div>
            </div>

            <div className="form-grid two">
              <div>
                <p className="text-sm font-semibold text-[#3b2f2f]">Henna Types</p>
                <p className="mt-2 text-sm text-muted-warm">
                  {getBookedHennaTypes(selectedBooking)}
                </p>
              </div>

              <div>
                <p className="text-sm font-semibold text-[#3b2f2f]">Booking Total</p>
                <p className="mt-2 text-sm text-muted-warm">
                  {selectedBooking?.bookingPrice !== undefined &&
                  selectedBooking?.bookingPrice !== null
                    ? formatCurrency(selectedBooking.bookingPrice)
                    : "—"}
                </p>
              </div>
            </div>

            <div className="form-grid two">
              <div>
                <p className="text-sm font-semibold text-[#3b2f2f]">Date</p>
                <p className="mt-2 text-sm text-muted-warm">
                  {selectedBooking?.bookingDate
                    ? formatDate(selectedBooking.bookingDate)
                    : "—"}
                </p>
              </div>

              <div>
                <p className="text-sm font-semibold text-[#3b2f2f]">Time</p>
                <p className="mt-2 text-sm text-muted-warm">
                  {selectedBooking?.startTime && selectedBooking?.endTime
                    ? `${formatTime(selectedBooking.startTime)} - ${formatTime(
                        selectedBooking.endTime
                      )}`
                    : "—"}
                </p>
              </div>
            </div>

            <div className="form-grid two">
              <div>
                <p className="text-sm font-semibold text-[#3b2f2f]">Party Size</p>
                <p className="mt-2 text-sm text-muted-warm">
                  {selectedBooking?.partySize ?? "—"}
                </p>
              </div>

              <div>
                <p className="text-sm font-semibold text-[#3b2f2f]">
                  Appointment Type
                </p>
                <p className="mt-2 text-sm text-muted-warm">
                  {selectedBooking?.serviceMode || "—"}
                </p>
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-[#3b2f2f]">
                Total Duration
              </p>
              <p className="mt-2 text-sm text-muted-warm">
                {selectedBooking?.bookingDurationMinutes !== undefined &&
                selectedBooking?.bookingDurationMinutes !== null
                  ? `${selectedBooking.bookingDurationMinutes} minutes`
                  : "—"}
              </p>
            </div>

            <div className="rounded-2xl bg-soft-cream p-4">
              <p className="text-sm font-semibold text-henna">Service Line Items</p>

              <div className="mt-4 space-y-3">
                {normalizeBookingItems(selectedBooking).map((item, index) => (
                  <div
                    key={`${selectedBooking.id}-item-${index}`}
                    className="rounded-xl bg-white p-4"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-[#3b2f2f]">
                          {item?.service?.serviceName || `Service ${index + 1}`}
                        </p>
                        <div className="mt-2 space-y-1 text-sm text-muted-warm">
                          <p>
                            Henna Type:{" "}
                            {item?.hennaType || item?.service?.hennaType || "—"}
                          </p>
                          <p>Adults: {item?.numOfAdults ?? 0}</p>
                          <p>Children: {item?.numOfChildren ?? 0}</p>
                          <p>Party Size: {item?.partySize ?? 0}</p>
                        </div>
                      </div>

                      <div className="text-right">
                        <p className="text-sm font-medium text-[#3b2f2f]">
                          {item?.bookingPrice !== undefined &&
                          item?.bookingPrice !== null
                            ? formatCurrency(item.bookingPrice)
                            : "—"}
                        </p>
                        <p className="mt-1 text-xs text-muted-warm">
                          {item?.bookingDurationMinutes !== undefined &&
                          item?.bookingDurationMinutes !== null
                            ? `${item.bookingDurationMinutes} minutes`
                            : "—"}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {selectedBooking?.serviceMode === SERVICE_MODE.MOBILE &&
            selectedBooking?.onsiteAddress ? (
              <div>
                <p className="text-sm font-semibold text-[#3b2f2f]">
                  Onsite Address
                </p>
                <p className="mt-2 text-sm text-muted-warm">
                  {buildOnsiteAddressText(selectedBooking.onsiteAddress)}
                </p>
              </div>
            ) : null}

            {selectedBooking?.locationNotes ? (
              <div>
                <p className="text-sm font-semibold text-[#3b2f2f]">
                  Location Notes
                </p>
                <p className="mt-2 text-sm leading-6 text-muted-warm">
                  {selectedBooking.locationNotes}
                </p>
              </div>
            ) : null}

            {selectedBooking?.notes ? (
              <div>
                <p className="text-sm font-semibold text-[#3b2f2f]">Notes</p>
                <p className="mt-2 text-sm leading-6 text-muted-warm">
                  {selectedBooking.notes}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={Boolean(confirmAction)}
        onClose={() => {
          if (!actionLoading) setConfirmAction(null);
        }}
        title={
          confirmAction?.type === "COMPLETE"
            ? "Mark Booking as Completed"
            : "Cancel Booking"
        }
        size="sm"
        footer={
          <div className="flex flex-col gap-3 md:flex-row md:justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setConfirmAction(null)}
              disabled={actionLoading}
            >
              Back
            </Button>

            <Button
              type="button"
              variant={confirmAction?.type === "COMPLETE" ? "primary" : "danger"}
              onClick={handleStatusAction}
              loading={actionLoading}
            >
              {confirmAction?.type === "COMPLETE"
                ? "Confirm Completion"
                : "Confirm Cancellation"}
            </Button>
          </div>
        }
      >
        <p className="text-sm leading-6 text-muted-warm">
          {confirmAction?.type === "COMPLETE"
            ? "Are you sure you want to mark this booking as completed?"
            : "Are you sure you want to cancel this booking?"}
        </p>
      </Modal>
    </div>
  );
};

export default ManageBookings;